// @flow

import React from 'react';
import { connect } from 'react-redux';
import changeCarouselSlotIndex from 'app/common/redux/actions/pin/carousel';
import getCarouselData from 'app/common/react/components/common/Carousel/utils/getCarouselData';
import i18n from 'app/common/lib/i18n';
import type { CarouselPinData } from 'app/packages/pinterest-flow-types/pin';
import type { Dispatch } from 'redux';
import type { Pin } from 'app/common/react/components/closeup/types';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import { getCommerceAuxData } from 'app/common/lib/CommerceUtil';
import { track } from 'app/common/lib/react/analytics';
import { Box, Icon } from 'gestalt';

type ReduxStateProps = {|
  carouselData?: ?CarouselPinData,
  pin?: Pin,
|};

type ReduxDispatchProps = {|
  changeCarouselSlotIndex: (pinId: string, newIndex: number) => void,
|};

type OwnProps = {|
  carouselData?: ?CarouselPinData,
  carouselIndex?: number,
  componentType?: number,
  contextLogData?: Object,
  handleCarouselSwipe?: (index: number) => void,
  pinId: string,
  viewParameter?: number,
  viewType?: number,
  isEditMode?: boolean,
|};

type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
  ...ReduxDispatchProps,
|};

const styles = {
  slider: {
    cursor: 'pointer',
    height: '100%',
    position: 'relative',
    width: '10%',
    zIndex: '105',
    pointerEvents: 'auto',
  },
};

const logSwipe = (
  pin: Pin,
  pinId: string,
  currentIndex: number,
  nextIndex: number,
  carouselData: CarouselPinData,
  viewParameter?: number,
  viewType?: number,
  componentType?: number,
  contextLogData?: Object,
  isEditMode?: boolean,
) => {
  if (!isEditMode || !carouselData) {
    const { carousel_slots, id: carouselId } = carouselData;
    track(PCONST.thrift.EventType.SWIPE, {
      ...contextLogData,
      objectId: pinId,
      component: componentType,
      view: viewType,
      viewParameter,
      eventData: {
        pinCarouselSlotEventData: {
          carouselSlotId: carousel_slots[currentIndex] && Number(carousel_slots[currentIndex].id),
          carouselDataId: Number(carouselId),
          carouselSlotIndex: currentIndex,
          toCarouselSlotIndex: nextIndex,
        },
      },
      ...(getCommerceAuxData(pin) || {}),
    });
  }
};

const propagationBlocker = (
  event: SyntheticEvent<>,
  pinId: string,
  index: number,
  changeCarouselSlotIndexFn?: (pinId: string, index: number) => void,
  handleCarouselSwipeFn?: (index: number) => void,
  isEditMode?: boolean,
) => {
  if (!isEditMode && typeof changeCarouselSlotIndexFn !== 'undefined') {
    event.preventDefault();
    event.stopPropagation();
    changeCarouselSlotIndexFn(pinId, index);
  }

  // handles case of boards using their own state
  if (typeof handleCarouselSwipeFn !== 'undefined') {
    handleCarouselSwipeFn(index);
  }
};

export const CarouselSliders = ({
  carouselIndex,
  carouselData,
  changeCarouselSlotIndex,
  handleCarouselSwipe,
  pin,
  pinId,
  contextLogData,
  viewParameter,
  viewType,
  componentType,
  isEditMode,
}: Props) => {
  if (!carouselData) {
    return null;
  }

  const { carousel_slots, index } = carouselData;
  // handles case of closeup having it's own state
  const indexToUse = typeof carouselIndex !== 'undefined' ? carouselIndex : index;
  const swipeLeft = Math.max(0, indexToUse - 1);
  const swipeRight = Math.min(carousel_slots.length - 1, indexToUse + 1);

  return (
    <Box display="flex" justifyContent="between" position="absolute" left right bottom top>
      <div
        data-test-id="carousel-slider-left"
        role="presentation"
        onClick={(event: SyntheticEvent<>) => {
          propagationBlocker(
            event,
            pinId,
            swipeLeft,
            changeCarouselSlotIndex,
            handleCarouselSwipe,
            isEditMode,
          );
          logSwipe(
            pin,
            pinId,
            indexToUse,
            swipeLeft,
            carouselData,
            viewParameter,
            viewType,
            componentType,
            contextLogData,
            isEditMode,
          );
        }}
        onKeyUp={(event: SyntheticEvent<>) => {
          propagationBlocker(
            event,
            pinId,
            swipeLeft,
            changeCarouselSlotIndex,
            handleCarouselSwipe,
            isEditMode,
          );
          logSwipe(
            pin,
            pinId,
            indexToUse,
            swipeLeft,
            carouselData,
            viewParameter,
            viewType,
            componentType,
            contextLogData,
            isEditMode,
          );
        }}
        style={styles.slider}
      >
        {indexToUse > 0 && (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon
              color="white"
              icon="arrow-back"
              accessibilityLabel={i18n._(
                'View Previous',
                'Button to advance carousel to the previous image',
              )}
            />
          </Box>
        )}
      </div>
      <div
        data-test-id="carousel-slider-right"
        role="presentation"
        onClick={(event: SyntheticEvent<>) => {
          propagationBlocker(
            event,
            pinId,
            swipeRight,
            changeCarouselSlotIndex,
            handleCarouselSwipe,
            isEditMode,
          );
          logSwipe(
            pin,
            pinId,
            indexToUse,
            swipeRight,
            carouselData,
            viewParameter,
            viewType,
            componentType,
            contextLogData,
            isEditMode,
          );
        }}
        onKeyUp={(event: SyntheticEvent<>) => {
          propagationBlocker(
            event,
            pinId,
            swipeRight,
            changeCarouselSlotIndex,
            handleCarouselSwipe,
            isEditMode,
          );
          logSwipe(
            pin,
            pinId,
            indexToUse,
            swipeRight,
            carouselData,
            viewParameter,
            viewType,
            componentType,
            contextLogData,
            isEditMode,
          );
        }}
        style={styles.slider}
      >
        {indexToUse < carousel_slots.length - 1 && (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon
              color="white"
              icon="arrow-forward"
              accessibilityLabel={i18n._(
                'View Next',
                'Button to advance carousel to the next image',
              )}
            />
          </Box>
        )}
      </div>
    </Box>
  );
};

const mapStateToProps = (
  { pins }: ReduxState,
  { carouselData: carouselDataProp, pinId }: OwnProps,
): ReduxStateProps => {
  const pin = pins[pinId];
  return {
    pin,
    carouselData: carouselDataProp || getCarouselData(pin),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps => ({
  changeCarouselSlotIndex: (pinId, newIndex) => dispatch(changeCarouselSlotIndex(pinId, newIndex)),
});

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(CarouselSliders);
